import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { isNullOrUndefined } from "utils/validations";
import { showAlertServiceError } from "utils/alerts";
import { initialPaginationStandarThousandItems } from "utils/initialPaginationsConfig";
import { enumsActionsTypes, enumsFormStatus, HttpStatus } from "utils/enums";
import { sortByPropertyName } from "utils/sortHelpers";
import useForm from "hooks/useForm";

const FormsContext = React.createContext(undefined);

export function FormsContextProvider({ children }) {
  const { getFormsById, getForms } = useForm();
  const [infoCurrentform, setInfoCurrentForm] = useState({});
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [search, setSearch] = useState("");
  const [formsDinamic, setFormsDinamic] = useState([]);
  const [isNotActiveFormRelatedTemplate, setIsNotActiveFormRelatedTemplate] = useState(false);

  /**
   * Fetches forms by their UUID and performs a specified action on the response.
   * This function sets a loading state while fetching data and updates the current form information
   * if the response contains valid data. If the response data is null or undefined, it triggers an
   * alert for a service error. The loading state is reset after the operation completes.
   * @param {string} formUuid - The unique identifier of the form to be fetched.
   * @param {Function} action - The action to be performed on the fetched form data.
   */
  const getFormsByIds = useCallback((formUuid, action) => {
    setIsLoadingForms(true);
    getFormsById(formUuid, action)
      .then((response) => {
        if (isNullOrUndefined(response.data) === false) {
          setInfoCurrentForm(response.data);
        } else {
          showAlertServiceError();
        }
      })
      .finally(() => setIsLoadingForms(false));
  }, []);

  /**
   * Fetches and processes active forms from the server.
   * Utilizes initial pagination settings to retrieve forms, sorts them by name,
   * and filters for active forms. Updates the state with active forms' UUIDs
   * and names. Handles errors by showing an alert for service errors and
   * manages loading state throughout the process.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page for pagination.
   * @param {string} search - The search query for filtering forms.
   * @param {number} FORM_STATUS_NULL - The status code representing a null form status.
   * @param {number} FORMS_STATUS_ACTIVE - The status code representing an active form status.
   */
  const getActiveForms = useCallback((isEdit, formSelected) => {
    const { page, per_page } = initialPaginationStandarThousandItems;
    const { FORM_STATUS_NULL, FORMS_STATUS_ACTIVE } = enumsFormStatus;
    setIsLoadingForms(true);
    setFormsDinamic([]);
    getForms(page, per_page, search, FORM_STATUS_NULL, enumsActionsTypes.ALL_ACTIONS)
      .then((response) => {
        if (isNullOrUndefined(response.data.items) === false) {
          const listForms = response.data.items;
          const orderListForms = sortByPropertyName(listForms, "name");

          let formsToShow = [];

          if (isEdit === false || isNullOrUndefined(isEdit) === true) {
            formsToShow = orderListForms.filter(function (form) {
              return form.status === FORMS_STATUS_ACTIVE;
            });
          } else {
            formsToShow = orderListForms.filter(function (form) {
              return form.status === FORMS_STATUS_ACTIVE;
            });

            if (isNullOrUndefined(formSelected) === false) {
              const selectedForm = orderListForms.find(function (form) {
                return form.uuid === formSelected;
              });
              if (
                selectedForm &&
                !formsToShow.some(function (form) {
                  return form.uuid === formSelected;
                })
              ) {
                formsToShow.push(selectedForm);

                if (selectedForm.status !== FORMS_STATUS_ACTIVE) {
                  setIsNotActiveFormRelatedTemplate(true);
                } else {
                  setIsNotActiveFormRelatedTemplate(false);
                }
              } else if (selectedForm && selectedForm.status !== FORMS_STATUS_ACTIVE) {
                setIsNotActiveFormRelatedTemplate(true);
              } else {
                setIsNotActiveFormRelatedTemplate(false);
              }
            } else {
              setIsNotActiveFormRelatedTemplate(false);
            }
          }

          setFormsDinamic(
            formsToShow.map(function (form) {
              return {
                value: form.uuid,
                label: form.name,
              };
            })
          );
        } else {
          showAlertServiceError();
        }
      })
      .catch(function (error) {
        if (error.response && error.response.status === HttpStatus.UNAUTHORIZED) {
          return;
        }
      })
      .finally(function () {
        setIsLoadingForms(false);
      });
  }, []);

  return (
    <FormsContext.Provider
      value={{
        getFormsByIds,
        getActiveForms,
        infoCurrentform,
        isLoadingForms,
        search,
        setSearch,
        formsDinamic,
        setFormsDinamic,
        isNotActiveFormRelatedTemplate,
      }}
    >
      {children}
    </FormsContext.Provider>
  );
}

FormsContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
};
export function useFormsContext() {
  const context = React.useContext(FormsContext);
  if (isNullOrUndefined(context) === true) {
    throw new Error("useFormsContext must be inside the FormsContext provider.");
  }
  return context;
}
